import { useState, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import toast from 'react-hot-toast';

/**
 * Image upload component with preview and caption
 * Uploads to Firebase Storage
 * 
 * @param {Function} onUpload - Callback when upload completes: onUpload({ url, caption })
 * @param {string} storagePath - Firebase Storage path (e.g., 'content-images')
 * @param {Object} currentImage - Current image data: { url, caption }
 */
export default function ImageUploadField({ onUpload, storagePath = 'content-images', currentImage = null }) {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(currentImage?.url || null);
  const [caption, setCaption] = useState(currentImage?.caption || '');
  const [previewUrl, setPreviewUrl] = useState(currentImage?.url || null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    // Show preview immediately
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);

    // Upload to Firebase Storage
    setUploading(true);
    try {
      const timestamp = Date.now();
      const filename = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const storageRef = ref(storage, `${storagePath}/${filename}`);
      
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      setImageUrl(url);
      setUploading(false);
      toast.success('Image uploaded successfully!');

      // Call parent callback
      onUpload({ url, caption });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image. Please try again.');
      setUploading(false);
      setPreviewUrl(null);
    }
  };

  const handleCaptionChange = (newCaption) => {
    setCaption(newCaption);
    if (imageUrl) {
      onUpload({ url: imageUrl, caption: newCaption });
    }
  };

  const handleRemove = () => {
    setImageUrl(null);
    setPreviewUrl(null);
    setCaption('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onUpload(null);
  };

  return (
    <div className="space-y-4">
      {/* Upload Button / Preview */}
      {!previewUrl ? (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="image-upload"
          />
          <label
            htmlFor="image-upload"
            className="block w-full border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
          >
            <svg className="w-12 h-12 mx-auto mb-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-text-primary font-semibold mb-1">
              Click to upload image
            </p>
            <p className="text-sm text-text-secondary">
              PNG, JPG, WebP up to 5MB
            </p>
          </label>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Image Preview */}
          <div className="relative rounded-xl overflow-hidden border-2 border-border">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full aspect-video object-cover"
            />
            
            {/* Uploading overlay */}
            {uploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-2" />
                  <p className="font-semibold">Uploading...</p>
                </div>
              </div>
            )}

            {/* Remove button */}
            {!uploading && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-3 right-3 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Caption Input */}
          {!uploading && (
            <div>
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Image Caption (optional)
              </label>
              <input
                type="text"
                value={caption}
                onChange={(e) => handleCaptionChange(e.target.value)}
                placeholder="Add a caption for this image"
                className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
              />
              <p className="text-xs text-text-secondary mt-1">
                This caption will be shown with the image
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


